import React, { createContext, forwardRef, useContext, useMemo } from 'react'
import { Pressable, Text, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { FOCUS_RING_CLASS } from '../../focusRing'

// Prop-driven AOS Radio (Figma RadioButton, node 7419:109977).
//
// NOTE: this does NOT wrap `_gluestack/radio`. The vendored gluestack radio is
// only clickable on web through react-aria's hidden `<input>` (its `.web.jsx`
// path), which the host Storybook / RN-web bundle doesn't take — so the View
// root never toggles the group (uncheckable, no hover/press). Radio also can't
// use the checkbox's "custom group → standalone" trick because react-aria radio
// requires a group. So this is a self-contained controlled radio on a real
// `Pressable`: `onPress` works everywhere and the `group/radio` CSS variants
// drive hover/press. States: unselected `action/grey-action` ring → checked
// `action` ring + dot → hover grey-action-hover / action-hover → pressed
// action-pressed → disabled 40% opacity + not interactive.

type RadioGroupContextValue = {
    value?: string
    onChange?: (value: string) => void
    isDisabled?: boolean
}
const RadioGroupContext = createContext<RadioGroupContextValue | null>(null)

type RadioItemContextValue = { checked: boolean; disabled: boolean }
const RadioItemContext = createContext<RadioItemContextValue>({ checked: false, disabled: false })

export type RadioGroupProps = {
    value?: string
    onChange?: (value: string) => void
    isDisabled?: boolean
    className?: string
    children?: React.ReactNode
}
export const RadioGroup = forwardRef<React.ComponentRef<typeof View>, RadioGroupProps>(
    ({ value, onChange, isDisabled, className, children }, ref) => {
        const ctx = useMemo(() => ({ value, onChange, isDisabled }), [value, onChange, isDisabled])
        return (
            <RadioGroupContext.Provider value={ctx}>
                <View ref={ref} className={twMerge('gap-3', className)} role='radiogroup'>
                    {children}
                </View>
            </RadioGroupContext.Provider>
        )
    },
)
RadioGroup.displayName = 'RadioGroup'

export type RadioProps = {
    value: string
    isDisabled?: boolean
    className?: string
    testID?: string
    children?: React.ReactNode
}
export const Radio = forwardRef<React.ComponentRef<typeof Pressable>, RadioProps>(
    ({ value, isDisabled, className, testID, children }, ref) => {
        const group = useContext(RadioGroupContext)
        const checked = group?.value === value
        const disabled = group?.isDisabled ?? isDisabled ?? false
        const itemCtx = useMemo(() => ({ checked, disabled }), [checked, disabled])
        return (
            <Pressable
                ref={ref}
                role='radio'
                accessibilityRole='radio'
                accessibilityState={{ checked, disabled }}
                disabled={disabled}
                onPress={() => group?.onChange?.(value)}
                testID={testID}
                className={twMerge(
                    'group/radio flex-row items-center gap-3 web:cursor-pointer',
                    FOCUS_RING_CLASS,
                    disabled && 'opacity-40 pointer-events-none web:cursor-not-allowed',
                    className,
                )}
            >
                <RadioItemContext.Provider value={itemCtx}>{children}</RadioItemContext.Provider>
            </Pressable>
        )
    },
)
Radio.displayName = 'Radio'

export type RadioIndicatorProps = { className?: string }
export const RadioIndicator = forwardRef<React.ComponentRef<typeof View>, RadioIndicatorProps>(
    ({ className }, ref) => {
        const { checked } = useContext(RadioItemContext)
        return (
            <View
                ref={ref}
                className={twMerge(
                    'h-5 w-5 items-center justify-center rounded-full border-2',
                    checked
                        ? 'border-action-default group-hover/radio:border-action-hover'
                        : 'border-action-grey-action group-hover/radio:border-action-grey-action-hover',
                    'group-active/radio:border-action-pressed',
                    className,
                )}
            >
                {checked && <View className='h-3 w-3 rounded-full bg-action-default' />}
            </View>
        )
    },
)
RadioIndicator.displayName = 'RadioIndicator'

export type RadioLabelProps = {
    className?: string
    children?: React.ReactNode
}
export const RadioLabel = forwardRef<React.ComponentRef<typeof Text>, RadioLabelProps>(
    ({ className, children }, ref) => (
        <Text
            ref={ref}
            className={twMerge(
                'text-[0.875rem] leading-[1.25rem] text-aos-text-default web:select-none',
                className,
            )}
        >
            {children}
        </Text>
    ),
)
RadioLabel.displayName = 'RadioLabel'

// Kept for API parity (the dot is rendered by RadioIndicator). Renders the
// action-blue dot if composed manually.
export type RadioIconProps = { className?: string }
export const RadioIcon = forwardRef<React.ComponentRef<typeof View>, RadioIconProps>(
    ({ className }, ref) => (
        <View ref={ref} className={twMerge('h-3 w-3 rounded-full bg-action-default', className)} />
    ),
)
RadioIcon.displayName = 'RadioIcon'
